import { useEffect, useState } from 'react';
import { PACKAGE_TABS, packageListParams } from '@mypoz/shared';
import type { PackageListQuery, PackageTab } from '@mypoz/shared';

import { fetchPackagePage } from '@/features/packages/api.js';

export interface PackageListPage {
  /** Vendor rows on this page — normalise with `usePackages().toPackages`. */
  readonly rows: readonly unknown[];
  /** The vendor's total for the current filters. */
  readonly total: number;
  /** Every tab counted with the same search and goods type. */
  readonly tabCounts: Readonly<Record<PackageTab, number>>;
  /**
   * `loading` only before the first answer; a later page change keeps showing the last page with
   * `refreshing` set, so the list does not blank out between pages.
   */
  readonly status: 'loading' | 'ready' | 'error';
  readonly refreshing: boolean;
}

const NO_COUNTS: Record<PackageTab, number> = {
  all: 0,
  on_the_way: 0,
  in_warehouse: 0,
  in_order: 0,
};

/**
 * One page of the Packages list, asked of the vendor (MYP-126), plus the four tab counts.
 *
 * The page is one request. The tab counts are four more — one `limit: 1` request per tab, of
 * which only the vendor's `count` is read — asked only when the search or goods type changes, as
 * the client-side tabs always followed them. `version` re-asks both after an add, edit or delete.
 */
export function usePackageListPage(
  query: PackageListQuery,
  page: number,
  size: number,
  version: number,
): PackageListPage & { retry: () => void } {
  const [answer, setAnswer] = useState<{ rows: readonly unknown[]; total: number }>();
  const [tabCounts, setTabCounts] = useState<Record<PackageTab, number>>();
  const [failed, setFailed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const { tab, search, goodsType, notOrdered } = query;

  useEffect(() => {
    let current = true;
    setRefreshing(true);
    setFailed(false);

    fetchPackagePage(packageListParams({ tab, search, goodsType, notOrdered }, page, size)).then(
      (result) => {
        if (!current) return;
        setAnswer({ rows: result.rows, total: result.count });
        setRefreshing(false);
      },
      (cause: unknown) => {
        if (!current) return;
        console.error('[packages] could not load a page of packages', cause);
        setFailed(true);
        setRefreshing(false);
      },
    );

    return () => {
      current = false;
    };
  }, [tab, search, goodsType, notOrdered, page, size, version, attempt]);

  // The counts do not change with the page or the chosen tab, so they are asked again only when
  // the search, goods type or "not on an order" switch does (or after a write).
  useEffect(() => {
    let current = true;

    Promise.all(
      PACKAGE_TABS.map((each) =>
        fetchPackagePage(
          packageListParams({ tab: each, search, goodsType, notOrdered }, 1, 1, each),
        ),
      ),
    ).then(
      (results) => {
        if (!current) return;
        const next = { ...NO_COUNTS };
        PACKAGE_TABS.forEach((each, index) => {
          next[each] = results[index]?.count ?? 0;
        });
        setTabCounts(next);
      },
      (cause: unknown) => {
        if (!current) return;
        console.error('[packages] could not count packages by tab', cause);
        setFailed(true);
      },
    );

    return () => {
      current = false;
    };
  }, [search, goodsType, notOrdered, version, attempt]);

  const state: PackageListPage = {
    rows: answer?.rows ?? [],
    total: answer?.total ?? 0,
    tabCounts: tabCounts ?? NO_COUNTS,
    status: failed ? 'error' : answer && tabCounts ? 'ready' : 'loading',
    refreshing,
  };

  return { ...state, retry: () => setAttempt((previous) => previous + 1) };
}
